"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.spinnerKeyframes = void 0;
exports.Button = Button;
exports.Input = Input;
exports.Badge = Badge;
exports.OrderStatusBadge = OrderStatusBadge;
exports.Card = Card;
exports.Spinner = Spinner;
exports.EmptyState = EmptyState;
const jsx_runtime_1 = require("react/jsx-runtime");
const tokens_1 = require("./tokens");
const utils_1 = require("./utils");
const buttonVariantStyles = {
    primary: { background: tokens_1.colors.primary, color: tokens_1.colors.textInverse, border: 'none' },
    secondary: {
        background: tokens_1.colors.surface,
        color: tokens_1.colors.text,
        border: `1px solid ${tokens_1.colors.borderStrong}`,
    },
    ghost: { background: 'transparent', color: tokens_1.colors.text, border: 'none' },
    danger: { background: tokens_1.colors.danger, color: tokens_1.colors.textInverse, border: 'none' },
};
const buttonSizeStyles = {
    sm: { padding: `${tokens_1.spacing.xs}px ${tokens_1.spacing.sm}px`, fontSize: 13, minHeight: 32 },
    md: { padding: `${tokens_1.spacing.sm}px ${tokens_1.spacing.md}px`, fontSize: 14, minHeight: 40 },
    lg: { padding: `${tokens_1.spacing.sm}px ${tokens_1.spacing.lg}px`, fontSize: 16, minHeight: 48 },
    /** For POS and KDS: a target you can hit reliably without looking. */
    touch: {
        padding: `${tokens_1.spacing.md}px ${tokens_1.spacing.lg}px`,
        fontSize: 18,
        minHeight: tokens_1.touchTargetPx,
        fontWeight: 600,
    },
};
function Button({ variant = 'primary', size = 'md', isLoading = false, fullWidth = false, leftIcon, disabled, children, className, style, ...rest }) {
    const isDisabled = disabled || isLoading;
    return ((0, jsx_runtime_1.jsxs)("button", { type: "button", ...rest, disabled: isDisabled, "aria-busy": isLoading || undefined, className: (0, utils_1.cn)('restor-button', className), style: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: tokens_1.spacing.sm,
            borderRadius: tokens_1.radii.md,
            fontWeight: 500,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.6 : 1,
            width: fullWidth ? '100%' : undefined,
            transition: 'opacity 120ms ease, transform 120ms ease',
            ...buttonVariantStyles[variant],
            ...buttonSizeStyles[size],
            ...style,
        }, children: [isLoading ? (0, jsx_runtime_1.jsx)(Spinner, { size: 16 }) : leftIcon, children] }));
}
function Input({ label, error, hint, id, className, style, ...rest }) {
    const inputId = id ?? rest.name ?? undefined;
    const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
    return ((0, jsx_runtime_1.jsxs)("div", { className: (0, utils_1.cn)('restor-field', className), style: { display: 'grid', gap: tokens_1.spacing.xs }, children: [label && ((0, jsx_runtime_1.jsx)("label", { htmlFor: inputId, style: { fontSize: 13, fontWeight: 500, color: tokens_1.colors.text }, children: label })), (0, jsx_runtime_1.jsx)("input", { ...rest, id: inputId, "aria-invalid": error ? true : undefined, "aria-describedby": describedBy, style: {
                    padding: `${tokens_1.spacing.sm}px ${tokens_1.spacing.md}px`,
                    borderRadius: tokens_1.radii.md,
                    border: `1px solid ${error ? tokens_1.colors.danger : tokens_1.colors.border}`,
                    fontSize: 14,
                    color: tokens_1.colors.text,
                    background: tokens_1.colors.surface,
                    outlineColor: tokens_1.colors.primary,
                    ...style,
                } }), error ? ((0, jsx_runtime_1.jsx)("span", { id: `${inputId}-error`, style: { fontSize: 12, color: tokens_1.colors.danger }, children: error })) : hint ? ((0, jsx_runtime_1.jsx)("span", { id: `${inputId}-hint`, style: { fontSize: 12, color: tokens_1.colors.textMuted }, children: hint })) : null] }));
}
function Badge({ children, bg = tokens_1.colors.surfaceAlt, fg = tokens_1.colors.text, className }) {
    return ((0, jsx_runtime_1.jsx)("span", { className: (0, utils_1.cn)('restor-badge', className), style: {
            display: 'inline-flex',
            alignItems: 'center',
            padding: `2px ${tokens_1.spacing.sm}px`,
            borderRadius: tokens_1.radii.full,
            background: bg,
            color: fg,
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
        }, children: children }));
}
/** Order status pill using the shared status palette. */
function OrderStatusBadge({ status }) {
    const tone = tokens_1.orderStatusColors[status] ?? { bg: tokens_1.colors.surfaceAlt, fg: tokens_1.colors.text, label: status };
    return ((0, jsx_runtime_1.jsx)(Badge, { bg: tone.bg, fg: tone.fg, children: tone.label }));
}
function Card({ children, padding = tokens_1.spacing.md, className, style }) {
    return ((0, jsx_runtime_1.jsx)("div", { className: (0, utils_1.cn)('restor-card', className), style: {
            background: tokens_1.colors.surface,
            border: `1px solid ${tokens_1.colors.border}`,
            borderRadius: tokens_1.radii.lg,
            padding,
            ...style,
        }, children: children }));
}
function Spinner({ size = 20, color = 'currentColor' }) {
    return ((0, jsx_runtime_1.jsxs)("svg", { width: size, height: size, viewBox: "0 0 24 24", role: "status", "aria-label": "Loading", style: { animation: 'restor-spin 0.8s linear infinite' }, children: [(0, jsx_runtime_1.jsx)("circle", { cx: "12", cy: "12", r: "9", fill: "none", stroke: color, strokeOpacity: 0.25, strokeWidth: "3" }), (0, jsx_runtime_1.jsx)("path", { d: "M21 12a9 9 0 0 0-9-9", fill: "none", stroke: color, strokeWidth: "3", strokeLinecap: "round" })] }));
}
/** Keyframes the spinner needs; inject once per app. */
exports.spinnerKeyframes = '@keyframes restor-spin { to { transform: rotate(360deg); } }';
function EmptyState({ title, description, action, }) {
    return ((0, jsx_runtime_1.jsxs)("div", { style: { textAlign: 'center', padding: tokens_1.spacing['2xl'], color: tokens_1.colors.textMuted }, children: [(0, jsx_runtime_1.jsx)("p", { style: { margin: 0, fontSize: 16, fontWeight: 600, color: tokens_1.colors.text }, children: title }), description && (0, jsx_runtime_1.jsx)("p", { style: { margin: `${tokens_1.spacing.sm}px 0 0`, fontSize: 14 }, children: description }), action && (0, jsx_runtime_1.jsx)("div", { style: { marginTop: tokens_1.spacing.md }, children: action })] }));
}
//# sourceMappingURL=components.js.map